// =============================================================================
// FilterPresetMenu - Viste salvate dei filtri del calendario
// =============================================================================
// Un preset e' una combinazione di filtri (tipi risorsa + risorse + ricerca)
// salvata con un nome. Appartiene alla coppia utente-organizzazione: nessun
// altro membro la vede, e cambiando organizzazione l'elenco cambia.
//
// Applicare un preset non "blocca" i filtri: restano modificabili, e il menu si
// limita a segnalare che quello che si vede non coincide piu' con la vista
// salvata, offrendo di aggiornarla.

import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bookmark,
  BookmarkPlus,
  ChevronDown,
  Check,
  Pencil,
  Trash2,
  LayoutGrid,
  Save,
  X,
  Loader2,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useToast } from '../../../store/toastStore';
import { useTranslatedError } from '../../../hooks/useTranslatedError';
import {
  useCalendarPresets,
  useCreateCalendarPreset,
  useUpdateCalendarPreset,
  useDeleteCalendarPreset,
  type CalendarPreset,
  type CalendarPresetFilters,
} from '../../../api/hooks';
import {
  useSchedulerActions,
  useActiveFilterPresetId,
  useSelectedResourceTypes,
  useSelectedResourceIds,
  useResourceSearchTerm,
} from '../../../store/schedulerStore';

/**
 * Confronto fra filtri indipendente dall'ordine: la stessa selezione fatta in
 * ordine diverso e' la stessa vista, e segnalarla come "modificata" sarebbe
 * solo rumore.
 */
function sameFilters(a: CalendarPresetFilters, b: CalendarPresetFilters): boolean {
  const sameIdList = (x: string[] | null, y: string[] | null) => {
    if (x === null || y === null) return x === y;
    if (x.length !== y.length) return false;
    const sortedY = [...y].sort();
    return [...x].sort().every((id, i) => id === sortedY[i]);
  };
  return (
    sameIdList(a.resourceTypeIds, b.resourceTypeIds) &&
    sameIdList(a.resourceIds, b.resourceIds) &&
    a.searchTerm === b.searchTerm
  );
}

export function FilterPresetMenu() {
  const { t } = useTranslation('scheduler');
  const toast = useToast();
  const { getErrorMessage } = useTranslatedError();

  const [isOpen, setIsOpen] = useState(false);
  /** Nome in composizione: `null` = form chiuso, stringa = form aperto. */
  const [draftName, setDraftName] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const draftInputRef = useRef<HTMLInputElement>(null);

  const selectedResourceTypes = useSelectedResourceTypes();
  const selectedResourceIds = useSelectedResourceIds();
  const resourceSearchTerm = useResourceSearchTerm();
  const activePresetId = useActiveFilterPresetId();
  const { applyFilterPreset, setActiveFilterPresetId, resetFilters } = useSchedulerActions();

  const { data: presets = [], isLoading } = useCalendarPresets();
  const createPreset = useCreateCalendarPreset();
  const updatePreset = useUpdateCalendarPreset();
  const deletePreset = useDeleteCalendarPreset();
  const isSaving = createPreset.isPending || updatePreset.isPending;

  const currentFilters: CalendarPresetFilters = useMemo(
    () => ({
      resourceTypeIds: selectedResourceTypes,
      resourceIds: selectedResourceIds,
      searchTerm: resourceSearchTerm,
    }),
    [selectedResourceTypes, selectedResourceIds, resourceSearchTerm],
  );

  // Il preset attivo puo' essere stato cancellato altrove (o su un'altra
  // scheda): l'id persistito resta, ma l'elenco non lo contiene piu'.
  const activePreset = presets.find((p) => p.id === activePresetId) ?? null;
  const isDirty = activePreset ? !sameFilters(activePreset.filters, currentFilters) : false;
  const hasAnyFilter =
    currentFilters.resourceTypeIds !== null ||
    currentFilters.resourceIds.length > 0 ||
    currentFilters.searchTerm.length > 0;
  /** Nessun filtro e nessuna vista: e' esattamente cio' che "Mostra tutto" produce. */
  const isShowingAll = !hasAnyFilter && !activePresetId;

  // Chiudi con click fuori / Escape
  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  // Ogni apertura riparte pulita: i form a meta' di una sessione precedente
  // sarebbero solo confondenti.
  useEffect(() => {
    if (!isOpen) {
      setDraftName(null);
      setRenamingId(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (draftName !== null) draftInputRef.current?.focus();
  }, [draftName]);

  const handleApply = (preset: CalendarPreset) => {
    applyFilterPreset(preset.id, preset.filters);
    setIsOpen(false);
  };

  // "Mostra tutto" e' la voce che riporta allo stato senza filtri: azzera i tre
  // filtri e stacca la vista corrente, cosi' il bottone torna neutro.
  const handleShowAll = () => {
    resetFilters();
    setIsOpen(false);
  };

  const handleCreate = async () => {
    const name = (draftName ?? '').trim();
    if (!name) return;
    try {
      const created = await createPreset.mutateAsync({ name, filters: currentFilters });
      // La vista appena salvata diventa quella attiva: e' esattamente cio' che
      // l'utente ha davanti.
      setActiveFilterPresetId(created.id);
      setDraftName(null);
      setIsOpen(false);
      toast.success(t('presets.created', { name: created.name }));
    } catch (err) {
      toast.error(getErrorMessage(err, t('presets.saveError')));
    }
  };

  const handleUpdateFilters = async () => {
    if (!activePreset) return;
    try {
      await updatePreset.mutateAsync({ id: activePreset.id, filters: currentFilters });
      setIsOpen(false);
      toast.success(t('presets.updated', { name: activePreset.name }));
    } catch (err) {
      toast.error(getErrorMessage(err, t('presets.saveError')));
    }
  };

  const handleRename = async (preset: CalendarPreset) => {
    const name = renameValue.trim();
    if (!name || name === preset.name) {
      setRenamingId(null);
      return;
    }
    try {
      await updatePreset.mutateAsync({ id: preset.id, name });
      setRenamingId(null);
    } catch (err) {
      toast.error(getErrorMessage(err, t('presets.saveError')));
    }
  };

  const handleDelete = async (preset: CalendarPreset) => {
    try {
      await deletePreset.mutateAsync(preset.id);
      if (activePresetId === preset.id) setActiveFilterPresetId(null);
      toast.success(t('presets.deleted', { name: preset.name }));
    } catch (err) {
      toast.error(getErrorMessage(err, t('presets.deleteError')));
    }
  };

  const triggerLabel = activePreset ? activePreset.name : t('presets.button');

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={t('presets.title')}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors max-w-[14rem]',
          activePreset
            ? 'border-emerald-200 bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
            : 'border-gray-200 text-gray-700 hover:bg-gray-100',
        )}
      >
        <Bookmark className="w-4 h-4 flex-shrink-0" />
        <span className="truncate">{triggerLabel}</span>
        {isDirty && (
          <span
            className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0"
            title={t('presets.modified')}
          />
        )}
        <ChevronDown className="w-4 h-4 flex-shrink-0 opacity-60" />
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1"
        >
          <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {t('presets.title')}
          </div>

          {/* Uscita rapida da qualunque vista o filtro: e' lo stato di
              partenza del calendario, quindi sta in cima. */}
          <div className="px-1">
            <button
              role="menuitem"
              onClick={handleShowAll}
              className={cn(
                'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm',
                isShowingAll ? 'bg-emerald-50' : 'hover:bg-gray-50',
              )}
            >
              <Check
                className={cn(
                  'w-4 h-4 flex-shrink-0',
                  isShowingAll ? 'text-emerald-600' : 'text-transparent',
                )}
              />
              <LayoutGrid className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
              <span
                className={cn(
                  'truncate',
                  isShowingAll ? 'text-emerald-800 font-medium' : 'text-gray-700',
                )}
              >
                {t('presets.showAll')}
              </span>
            </button>
          </div>

          {presets.length > 0 && <div className="border-t border-gray-100 my-1" />}

          {isLoading && (
            <div className="px-3 py-3 text-sm text-gray-400 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('presets.loading')}
            </div>
          )}

          {!isLoading && presets.length === 0 && (
            <div className="px-3 pb-2 text-xs text-gray-400">{t('presets.empty')}</div>
          )}

          <div className="max-h-64 overflow-y-auto">
            {presets.map((preset) => {
              const isActive = preset.id === activePresetId;

              if (renamingId === preset.id) {
                return (
                  <div key={preset.id} className="flex items-center gap-1 px-2 py-1.5">
                    <input
                      autoFocus
                      value={renameValue}
                      maxLength={60}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRename(preset);
                        if (e.key === 'Escape') setRenamingId(null);
                      }}
                      className="flex-1 min-w-0 px-2 py-1 text-sm border border-emerald-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <button
                      onClick={() => handleRename(preset)}
                      className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                      title={t('presets.confirm')}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setRenamingId(null)}
                      className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                      title={t('presets.cancel')}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              }

              return (
                <div
                  key={preset.id}
                  className={cn(
                    'group flex items-center gap-1 px-2 py-1.5 rounded-md mx-1',
                    isActive ? 'bg-emerald-50' : 'hover:bg-gray-50',
                  )}
                >
                  <button
                    role="menuitem"
                    onClick={() => handleApply(preset)}
                    className="flex-1 min-w-0 flex items-center gap-2 text-left text-sm"
                  >
                    <Check
                      className={cn(
                        'w-4 h-4 flex-shrink-0',
                        isActive ? 'text-emerald-600' : 'text-transparent',
                      )}
                    />
                    <span
                      className={cn(
                        'truncate',
                        isActive ? 'text-emerald-800 font-medium' : 'text-gray-700',
                      )}
                    >
                      {preset.name}
                    </span>
                  </button>
                  {/* Sempre visibili, ma tenui: nascoste dietro l'hover
                      sarebbero irraggiungibili da touch e invisibili a chi non
                      sa che ci sono. */}
                  <button
                    onClick={() => {
                      setRenamingId(preset.id);
                      setRenameValue(preset.name);
                    }}
                    className="p-1 text-gray-300 hover:text-gray-700 hover:bg-gray-100 group-hover:text-gray-400 rounded transition-colors"
                    title={t('presets.rename')}
                    aria-label={`${t('presets.rename')}: ${preset.name}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(preset)}
                    disabled={deletePreset.isPending}
                    className="p-1 text-gray-300 hover:text-red-600 hover:bg-red-50 group-hover:text-gray-400 rounded transition-colors disabled:opacity-40"
                    title={t('presets.delete')}
                    aria-label={`${t('presets.delete')}: ${preset.name}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="border-t border-gray-100 mt-1 pt-1">
            {/* Aggiornamento in-place: compare solo quando c'e' davvero uno
                scarto fra la vista salvata e i filtri correnti. */}
            {activePreset && isDirty && (
              <button
                onClick={handleUpdateFilters}
                disabled={isSaving}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">
                  {t('presets.updateCurrent', { name: activePreset.name })}
                </span>
              </button>
            )}

            {draftName === null ? (
              <button
                onClick={() => setDraftName('')}
                disabled={!hasAnyFilter}
                title={hasAnyFilter ? undefined : t('presets.needFilters')}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <BookmarkPlus className="w-4 h-4 flex-shrink-0" />
                {t('presets.saveCurrent')}
              </button>
            ) : (
              <div className="px-2 py-1.5">
                <div className="flex items-center gap-1">
                  <input
                    ref={draftInputRef}
                    value={draftName}
                    maxLength={60}
                    placeholder={t('presets.namePlaceholder')}
                    onChange={(e) => setDraftName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreate();
                      if (e.key === 'Escape') setDraftName(null);
                    }}
                    className="flex-1 min-w-0 px-2 py-1 text-sm border border-emerald-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <button
                    onClick={handleCreate}
                    disabled={!draftName.trim() || isSaving}
                    className="p-1 text-emerald-600 hover:bg-emerald-50 rounded disabled:opacity-40"
                    title={t('presets.confirm')}
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => setDraftName(null)}
                    className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                    title={t('presets.cancel')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="mt-1 px-1 text-xs text-gray-400">{t('presets.saveHint')}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
